import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, Dumbbell, Heart, Wind, Target, TrendingUp, Clock, FileText } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

const RespiratoryMuscleTrainingPage = () => {
  const benefits = [
    {
      title: "Amélioration de la capacité respiratoire",
      description: "Augmentation du volume pulmonaire et de l'efficacité des échanges gazeux.",
      icon: Wind
    },
    {
      title: "Réduction de la dyspnée",
      description: "Diminution de la sensation d'essoufflement lors d'efforts physiques.",
      icon: Heart
    },
    {
      title: "Performance sportive",
      description: "Amélioration de l'endurance et de la récupération chez les athlètes.",
      icon: TrendingUp
    },
    {
      title: "Rééducation respiratoire",
      description: "Récupération post-COVID, BPCO, asthme et autres pathologies respiratoires.",
      icon: Target
    },
    {
      title: "Assouplissement thoracique",
      description: "Étirement du parenchyme pulmonaire, augmentation de l'élasticité (compliance), et en combinaison avec des mouvements de mobilisation du tronc et du thorax, assouplissement de la cage thoracique.",
      icon: Dumbbell
    },
    {
      title: "Amélioration du contrôle respiratoire",
      description: "Augmentation de la capacité à sentir ses muscles, et à comprendre instinctivement comment les activer ou désactiver.",
      icon: Target
    }
  ];

  const exercises = [
    {
      name: "Respiration diaphragmatique résistée",
      description: "Placer un poids léger sur l'abdomen et pratiquer la respiration diaphragmatique profonde.",
      duration: "5-10 minutes",
      frequency: "2-3x par jour"
    },
    {
      name: "Inspiratory Muscle Training (IMT)",
      description: "Utiliser un appareil de résistance inspiratoire pour renforcer le diaphragme et les muscles intercostaux.",
      duration: "30 respirations",
      frequency: "2x par jour"
    },
    {
      name: "Expiratory Muscle Training (EMT)",
      description: "Exercices de résistance à l'expiration pour renforcer les muscles abdominaux et intercostaux internes.",
      duration: "30 respirations",
      frequency: "2x par jour"
    },
    {
      name: "Respiration contre résistance labiale",
      description: "Expirer lentement à travers les lèvres pincées pour créer une résistance naturelle.",
      duration: "5-10 minutes",
      frequency: "Plusieurs fois par jour"
    },
    {
      name: "Le pince-nez",
      description: "Dans ma pratique quotidienne, j'ai essayé de trouver un moyen de réentraîner les muscles respiratoires avec un coût d'achat minimal pour les patients qui ne veulent ou ne peuvent pas investir dans des appareils du commerce. J'ai trouvé l'utilisation du pince-nez pertinente, d'autant plus qu'avec cet appareil, on respecte la physiologie en utilisant l'organe prévu pour la respiration.",
      instructions: "Placer le pince-nez de sorte qu'il permette toujours le passage de l'air, mais de façon plus restreinte. Déplacer le pince-nez vers le haut ou le bas pour augmenter ou diminuer la difficulté.",
      duration: "5-10 minutes",
      frequency: "2-3x par jour"
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFFFF", fontFamily: "Figtree, sans-serif" }}>
      <SiteHeaderV2 />
      
      {/* Hero Section with Title */}
      <section 
        className="py-12 relative"
        style={{
          backgroundColor: '#F8FAFC',
          
          
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
          {/* Title */}
          <PageTitle
            title="Entraînement des Muscles"
            highlight="Respiratoires"
            subtitle="Renforcer votre diaphragme et vos muscles respiratoires"
            align="center"
            className="mb-8"
          />
          
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 mb-6">
            <div className="w-64 h-64 md:w-80 md:h-80 rounded-full shadow-lg border-4 border-white/50 overflow-hidden flex-shrink-0">
              <img 
                src="/images/homme-entrainement-respiratoire.png"
                alt="Entraînement des muscles respiratoires"
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 20%' }}
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-xl text-slate-800 leading-relaxed font-medium">
                Les muscles respiratoires, comme tout autre muscle du corps, peuvent être renforcés 
                par un entraînement spécifique. Découvrez les techniques et exercices pour améliorer 
                votre force et endurance respiratoire.
              </p>
            </div>
          </div>
          
          {/* Document Button - Bottom Right */}
          <div className="flex justify-end mt-4">
            <Link
              to="/view-document?doc=/documents/renforcement_muscles_respiratoires.pdf&title=Guide%20de%20renforcement%20des%20muscles%20respiratoires"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white font-medium hover:bg-white/30 transition-all shadow-lg"
              style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}
            >
              <FileText size={18} />
              En savoir plus
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Pourquoi entraîner ses muscles respiratoires ?
          </h2>
          
          <div className="prose prose-lg max-w-none text-slate-600 space-y-4">
            <p>
              La respiration est un acte automatique, mais les muscles qui la permettent peuvent être 
              affaiblis par le manque d&apos;exercice, le vieillissement, certaines maladies ou une respiration 
              dysfonctionnelle chronique.
            </p>
            <p>
              L&apos;<strong>entraînement des muscles respiratoires (RMT - Respiratory Muscle Training)</strong> 
              est une technique validée scientifiquement qui permet de renforcer spécifiquement le diaphragme, 
              les muscles intercostaux et les muscles accessoires de la respiration.
            </p>
            <p>
              Cette approche est particulièrement bénéfique pour les personnes souffrant de maladies 
              respiratoires chroniques, les athlètes cherchant à optimiser leurs performances, et toute 
              personne souhaitant améliorer sa fonction respiratoire.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Les Bénéfices
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">{benefit.title}</h3>
                      <p className="text-slate-600">{benefit.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Exercises */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Exercices de Base
          </h2>
          
          <div className="space-y-6">
            {exercises.map((exercise, index) => (
              <Card key={index} className="border-0 shadow-md overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    <div className="flex-1 p-6">
                      <h3 className="text-xl font-medium mb-2">{exercise.name}</h3>
                      <p className="text-slate-600 mb-3">{exercise.description}</p>
                      {exercise.instructions && (
                        <p className="text-slate-700 mb-4 p-3 bg-cyan-50 border-l-4 border-sky-500 rounded-r-lg text-sm italic">
                          <strong className="not-italic">Instructions :</strong> {exercise.instructions}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-2 text-sky-700">
                          <Clock size={16} />
                          <span>{exercise.duration}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sky-600">
                          <TrendingUp size={16} />
                          <span>{exercise.frequency}</span>
                        </div>
                      </div>
                    </div>
                    <div className="w-full md:w-2 bg-gradient-to-b md:bg-gradient-to-r from-cyan-500 to-teal-500" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-16 bg-white/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Conseils Pratiques
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-cyan-50 border-l-4 border-sky-500 rounded-lg">
              <h4 className="font-bold text-sky-900 mb-2"> Progressivité</h4>
              <p className="text-sky-800 text-sm">
                Commencez avec une résistance faible et augmentez progressivement. 
                La surcharge progressive est la clé du renforcement musculaire.
              </p>
            </div>
            
            <div className="p-6 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
              <h4 className="font-medium mb-2">⏰ Régularité</h4>
              <p className="text-slate-700 text-sm">
              La constance est plus importante que l&apos;intensité. 
                Pratiquez quotidiennement pour des résultats optimaux.
              </p>
            </div>
            
            <div className="p-6 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
              <h4 className="font-bold text-sky-900 mb-2">🧘 Posture</h4>
              <p className="text-sky-800 text-sm">
                Maintenez une bonne posture pendant les exercices. 
                Une position assise droite ou debout facilite le travail du diaphragme.
              </p>
            </div>
            
            <div className="p-6 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
              <h4 className="font-bold text-sky-900 mb-2"> Suivi</h4>
              <p className="text-sky-800 text-sm">
                Notez vos progrès (nombre de répétitions, résistance utilisée). 
                Cela vous motivera et permettra d&apos;ajuster votre programme.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Guide Complet d&apos;Entraînement
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Téléchargez notre guide détaillé avec des programmes d&apos;entraînement progressifs, 
            des illustrations et des conseils pour optimiser votre pratique.
          </p>
          <Link 
            to="/view-document?doc=/documents/renforcement_muscles_respiratoires.pdf&title=Guide%20de%20renforcement%20des%20muscles%20respiratoires"
            className="inline-flex items-center gap-2 px-10 py-4 text-lg rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white hover:from-cyan-600 hover:to-teal-600 shadow-lg font-semibold transition-all"
          >
            <FileText size={22} />
            En savoir plus
          </Link>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default RespiratoryMuscleTrainingPage;
